import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';
import FormWrapper from '../FormWrapper';

const initialForm = {
  firstName: '', lastName: '', phone: '', 
  username: '', email: '', password: '', confirmPassword: '',
  classifierCode: '',
  emailCode: ''
};

const SignupForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailCountdown, setEmailCountdown] = useState(0);
  const [emailError, setEmailError] = useState('');
  const [error, setError] = useState('');

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'email') setEmailError('');
  };

  const handleSendEmailCode = async () => {
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setEmailError('فرمت ایمیل نامعتبر است');
      return;
    }
    setLoading(true);
    setEmailError('');
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.neontradepro.com'; 
      const response = await fetch(`${apiUrl}/auth/code/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email: form.email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'ایمیل نامعتبر است یا خطایی در سرور رخ داده.');
      }

      setEmailCodeSent(true);
      setEmailCountdown(120);

    } catch (error) {
      setEmailError((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailCode = async (code: string) => { 
    setLoading(true); 
    setEmailError(''); 
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.neontradepro.com'; 
      const response = await fetch(`${apiUrl}/auth/code/check`, { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email: form.email, code: parseInt(code, 10) }), 
      });
      const data = await response.json();

      if (!data.ok) { 
        throw new Error(data.detail || 'کد تایید نامعتبر است.'); 
      }

      setEmailVerified(true); 
      setForm(prev => ({ ...prev, emailCode: code })); 
    } catch (error) {
      setEmailError((error as Error).message); 
      setEmailVerified(false); 
    } finally {
      setLoading(false); 
    }
  };


  const handleNext = () => setStep(s => s + 1);
  const handlePrev = () => setStep(s => s - 1);

  const handleSubmit = async () => {
     setLoading(true);
    setError('');
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.neontradepro.com'; 
      let phoneNumber = form.phone;
      if (phoneNumber.startsWith('09')) {
        phoneNumber = '989' + phoneNumber.substring(2);
      }

      const response = await fetch(`${apiUrl}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          first_name: form.firstName,
          last_name: form.lastName,
          username: form.username,
          email: form.email,
          password: form.password,
          phone: phoneNumber, 
          referrer_code: form.classifierCode || undefined,
          email_code: parseInt(form.emailCode, 10)
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'خطا در ثبت نام');
      }
      localStorage.setItem('isAuthenticated', 'true');
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
      }
      
      navigate('/', { replace: true });
      window.location.reload();

    } catch (error) {
        const errorMessage = (error as Error).message;
        setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const titles = [
    "ایجاد حساب کاربری جدید",
    "اطلاعات حساب و ایمیل",
    "تنظیم رمز عبور",
    "تکمیل ثبت نام"
  ];
  const subtitles = [
    "مرحله ۱: اطلاعات شخصی خود را وارد کنید.",
    "مرحله ۲: نام کاربری و ایمیل خود را تایید کنید.",
    "مرحله ۳: یک رمز عبور امن برای حساب خود انتخاب کنید.",
    "مرحله ۴: در صورت داشتن کد معرف، آن را وارد نمایید."
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return <StepOne form={form} onChange={handleChange} onNext={handleNext} />;
      case 2:
        return <StepTwo
          form={form} onChange={handleChange} onNext={handleNext} onPrev={handlePrev}
          onSendEmailCode={handleSendEmailCode} emailCodeSent={emailCodeSent}
          emailVerified={emailVerified} onVerifyEmailCode={handleVerifyEmailCode}
          loading={loading} countdown={emailCountdown} setCountdown={setEmailCountdown}
          error={emailError} />;
      case 3:
        return <StepThree form={form} onChange={handleChange} onNext={handleNext} onPrev={handlePrev} />;
      case 4:
        return <StepFour form={form} onChange={handleChange} onSubmit={handleSubmit} onPrev={handlePrev} loading={loading} />;
      default:
        return null;
    }
  }

  return (
    <FormWrapper
      title={titles[step - 1]}
      subtitle={subtitles[step - 1]}
      currentStep={step}
      totalSteps={4}
    >
      {error ? (
        <div style={{ textAlign: 'center' }}>
            <div className='error-message'>{error}</div>
            <button className='main-btn' style={{ marginTop: '16px' }} onClick={() => { setError(''); setStep(1); }}>
                تلاش مجدد
            </button>
        </div>
      ) : renderStep()}
    </FormWrapper>
  );
};

export default SignupForm;